import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  Stack,
  Tooltip,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOpened from "assets/icons/templatesIcons/FolderOpened.svg";
import FolderClosed from "assets/icons/templatesIcons/FolderClosed.svg";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import { isTruthy, renderTooltipText } from "helpers/methods";

const FolderTreeComponent = ({
  folderData,
  destFolder,
  setDestFolder,
  selectedFolderData,
  isShowMoveFolderName,
}: any = []) => {
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const theme = useTheme();

  const toggleFolder = (folderId: any) => {
    const newExpanded = new Set(expandedFolders);
    newExpanded.has(folderId)
      ? newExpanded.delete(folderId)
      : newExpanded.add(folderId);
    setExpandedFolders(newExpanded);
  };

  const handleFolderSelect = (folder: any) => {
    setDestFolder(folder);
  };

  const handleFolderKeyDown = (
    event: React.KeyboardEvent,
    folder: any,
    isDisabled: boolean,
    hasSubFolders: boolean,
    isExpanded: boolean
  ) => {
    if (isDisabled) return;
    if (event.target !== event.currentTarget) return;
    const isRoot = folder.folderId === 0;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleFolderSelect(folder);
    } else if (
      event.key === "ArrowRight" &&
      hasSubFolders &&
      !isExpanded &&
      !isRoot
    ) {
      event.preventDefault();
      toggleFolder(folder.folderId);
    } else if (
      event.key === "ArrowLeft" &&
      hasSubFolders &&
      isExpanded &&
      !isRoot
    ) {
      event.preventDefault();
      toggleFolder(folder.folderId);
    }
  };

  const renderFolder = (folder: any, level = 0) => {
    const isExpanded = expandedFolders.has(folder.folderId);
    const hasSubFolders = folder.subFolders && folder.subFolders.length > 0;
    const isSelected = destFolder?.folderId === folder.folderId;
    const isSelfMove = selectedFolderData?.parentId === folder.folderId;
    const folderItSelf =
      selectedFolderData?.autoGeneratedId === folder.folderId;

    const isDisabled = isShowMoveFolderName && (isSelfMove || folderItSelf);

    return (
      <Box key={folder.folderId}>
        <Box
          role="treeitem"
          aria-selected={isSelected}
          aria-level={level + 1}
          aria-expanded={
            hasSubFolders ? isExpanded || folder.folderId === 0 : undefined
          }
          aria-disabled={isDisabled || undefined}
          display="flex"
          alignItems="center"
          py={0.75}
          tabIndex={isDisabled ? -1 : 0}
          onKeyDown={(e) =>
            handleFolderKeyDown(
              e,
              folder,
              isDisabled,
              hasSubFolders,
              isExpanded || folder.folderId === 0
            )
          }
          sx={(theme) => ({
            pl: `${level * 2 + 1}rem`,
            cursor: isDisabled ? "not-allowed" : "pointer",
            bgcolor: isSelected ? theme.palette.primary.light : "transparent",
            borderRight: isSelected
              ? `4px solid ${theme.palette.primary.main}`
              : "none",
            transition: "background-color 0.2s",
            opacity: isDisabled ? 0.9 : 1,
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: "-2px",
            },
            ...(!isDisabled && {
              "&:hover": { bgcolor: theme.palette.primary.light },
            }),
          })}
          onClick={() => {
            if (!isDisabled) {
              handleFolderSelect(folder);
            }
          }}
        >
          {hasSubFolders ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(folder.folderId);
              }}
              aria-label={
                isExpanded || folder.folderId === 0
                  ? `Collapse ${folder.name}`
                  : `Expand ${folder.name}`
              }
              sx={(theme) => ({
                mr: 1,
                "&.Mui-focusVisible": {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: "-2px",
                },
              })}
              disabled={isDisabled}
            >
              {isExpanded || folder.folderId === 0 ? (
                <ExpandMoreIcon />
              ) : (
                <ChevronRightIcon />
              )}
            </IconButton>
          ) : (
            <Box width={32} mr={1} />
          )}

          <Box mr={1.5}>
            {hasSubFolders && (isExpanded || folder.folderId === 0) ? (
              <img src={FolderOpened} width={20} alt="Opened folder icon" aria-hidden="true" />
            ) : (
              <img src={FolderClosed} width={20} alt="Closed folder icon" aria-hidden="true" />
            )}
          </Box>

          <Typography
            variant="body2"
            flexGrow={1}
            fontWeight={isSelected ? "500" : "400"}
            color={
              isSelected
                ? "primary.main"
                : isDisabled
                ? "text.disabled"
                : "text.primary"
            }
            textAlign="start"
          >
            {renderTooltipText(folder.name)}
          </Typography>
        </Box>

        {hasSubFolders && (isExpanded || folder.folderId === 0) && (
          <Box role="group">
            {folder.subFolders.map((subFolder: any) =>
              renderFolder(subFolder, level + 1)
            )}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Box border={`1px solid ${theme.palette.primary.main}`} borderRadius={"12px"}>
        <Box
          px={2}
          py={1.5}
          bgcolor="grey.100"
          borderBottom={`1px solid ${theme.palette.divider}`}
          borderRadius={"12px 12px 0px 0px"}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <img src={FolderClosed} width={20} alt="Folder icon" aria-hidden="true" />
            <Typography variant="subtitle1" fontWeight="600" color="primary.main">
              Folders
            </Typography>
          </Box>
        </Box>

        <Box
          role="tree"
          aria-label="Folders"
          sx={{
            position: "relative",
            height: "24rem",
            overflowY: "auto",
          }}
          borderRadius={"12px"}
        >
          {folderData.map((folder: any) => renderFolder(folder))}
        </Box>
      </Box>
      {isShowMoveFolderName && (
        <Stack
          direction="row"
          spacing={2}
          mt={2}
          alignItems="center"
          justifyContent="center"
        >
          {selectedFolderData && isTruthy(selectedFolderData.templateName) && (
            <Box
              px={1.25}
              py={0.75}
              borderRadius="10px"
              bgcolor="primary.light"
              border={`1px solid ${theme.palette.primary.main}`}
              display="flex"
              alignItems="center"
              gap={1}
              width={150}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  bgcolor: theme.palette.primary.alpha12,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {selectedFolderData.type === "template" ? (
                  <DescriptionRoundedIcon
                    sx={{ fontSize: 14, color: "primary.main" }}
                  />
                ) : (
                  <img
                    src={FolderClosed}
                    alt="Folder Icon"
                    width={14}
                    height={14}
                    style={{ display: "block" }}
                  />
                )}
              </Box>

              <Box sx={{ overflow: "hidden" }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.2, fontSize: 11 }}
                >
                  {selectedFolderData.type === "template"
                    ? "Template "
                    : selectedFolderData.type === "request"
                    ? "Request "
                    : "Folder "}
                  To move
                </Typography>

                <Tooltip title={selectedFolderData.templateName} arrow>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color="primary.main"
                    sx={{
                      lineHeight: 1.3,
                      fontSize: 12,
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      maxWidth: "8.75rem",
                      cursor: "default",
                      "@media (min-width: 1px)": {
                        "&:hover, &:focus": {
                          overflow: "visible",
                          textOverflow: "clip",
                          whiteSpace: "normal",
                          maxWidth: "none",
                          position: "relative",
                          zIndex: 1000,
                          backgroundColor: "background.paper",
                          padding: "4px",
                          borderRadius: "4px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        },
                      },
                    }}
                    className="zoom-friendly-text"
                  >
                    {selectedFolderData.templateName}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
          )}

          {destFolder && <TrendingFlatIcon sx={{ color: "primary.main" }} />}

          {destFolder && (
            <Box
              px={1.25}
              py={0.75}
              borderRadius="10px"
              bgcolor="primary.light"
              border={`1px solid ${theme.palette.primary.main}`}
              display="flex"
              alignItems="center"
              gap={1}
              width={150}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  bgcolor: theme.palette.primary.alpha12,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <img
                  src={FolderClosed}
                  alt="Folder Icon"
                  width={14}
                  height={14}
                  style={{ display: "block" }}
                />
              </Box>

              <Box sx={{ overflow: "hidden" }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.2, fontSize: 11 }}
                >
                  Target Folder
                </Typography>
                <Tooltip title={destFolder.name} arrow>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color="primary.main"
                    sx={{
                      lineHeight: 1.3,
                      fontSize: 12,
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      maxWidth: "8.75rem",
                      cursor: "default",
                      "@media (min-width: 1px)": {
                        "&:hover, &:focus": {
                          overflow: "visible",
                          textOverflow: "clip",
                          whiteSpace: "normal",
                          maxWidth: "none",
                          position: "relative",
                          zIndex: 1000,
                          backgroundColor: "background.paper",
                          padding: "4px",
                          borderRadius: "4px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        },
                      },
                    }}
                    className="zoom-friendly-text"
                  >
                    {destFolder.name}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default FolderTreeComponent;
